import type { FeatureKey } from '@/types';
import type { Plan, PlatformActor, PlatformSettings, AuditLogEntry, PlatformPayment, Subscription, TenantListRow } from '@/types/platform';
import type { TenantData } from '@/data/adminSeed';
import { buildReport } from '../admin/insights';
import { computeEffectiveFeatures } from '@/lib/featureRegistry';
import { PLAN_DEFS } from '@/data/plans';
import { ALL_TENANT_IDS, DEMO_TENANT_ID, SEED_NOW } from '@/data/adminSeed';
import { PLATFORM_ROLE_PERMISSIONS, isSuperAdmin, canPlatform, type PlatformPermission } from '@/lib/permissions';
import { ServiceError } from '../../contracts';
import { db, delay, uid as adminUid, shouldFail } from '../db';
import { adminRoot, dataFor, saveAdmin } from '../admin/context';

export { delay, DEMO_TENANT_ID, ALL_TENANT_IDS, adminRoot, dataFor, saveAdmin };

/* --------------------------------------------------------------------------------------------
   Platform-owned mock store: plans, audit log, platform settings. Tenant records themselves stay in
   `services/mock/admin/context.ts` (`adminRoot()`) — there is exactly one copy of each tenant, this module
   only adds the platform-level data layered on top of it (mirrors a real system: a `plans`/`audit_log`/
   `platform_settings` set of tables next to the existing multi-tenant `tenants` table).
-------------------------------------------------------------------------------------------- */
interface PlatformStore {
  plans: Plan[];
  subscriptions: Subscription[];
  payments: PlatformPayment[];
  auditLog: AuditLogEntry[];
  settings: PlatformSettings;
}
const KEY = 'mce.mock.platform.v1';
const DAY = 86_400_000;
let root: PlatformStore | undefined;

function seedPlans(): Plan[] {
  const now = new Date('2025-01-01T00:00:00Z').toISOString();
  return PLAN_DEFS.map((p) => ({ ...p, active: true, createdAt: now, updatedAt: now }));
}

/** One subscription per tenant + up to 6 months of billing history, derived from each tenant's plan/status. */
function seedBilling(plans: Plan[]): { subscriptions: Subscription[]; payments: PlatformPayment[] } {
  const subscriptions: Subscription[] = [];
  const payments: PlatformPayment[] = [];
  ALL_TENANT_IDS.forEach((tid) => {
    const t = dataFor(tid);
    const plan = plans.find((p) => p.id === t.planId) ?? plans[0];
    const status: Subscription['status'] = t.status === 'TRIAL' ? 'TRIALING' : t.status === 'SUSPENDED' ? 'PAST_DUE' : 'ACTIVE';
    const periodStart = SEED_NOW - 6 * DAY;
    subscriptions.push({
      id: `sub_${tid}`, tenantId: tid, tenantName: t.name, planId: plan.id, planName: plan.name, status, cycle: 'MONTHLY',
      amount: plan.priceMonthly, currentPeriodStart: new Date(periodStart).toISOString(), currentPeriodEnd: new Date(periodStart + 30 * DAY).toISOString(),
      cancelAtPeriodEnd: false, createdAt: t.createdAt,
    });
    if (plan.priceMonthly <= 0 || t.status === 'TRIAL') return; // free plan or still trialing: nothing billed yet
    const monthsActive = Math.max(1, Math.min(6, Math.round((SEED_NOW - Date.parse(t.createdAt)) / (30 * DAY))));
    for (let m = monthsActive; m >= 1; m--) {
      const at = SEED_NOW - m * 30 * DAY;
      const failed = t.status === 'SUSPENDED' && m === 1; // the most recent invoice is what triggered the suspension
      payments.push({
        id: `pay_${tid}_${m}`, tenantId: tid, tenantName: t.name, invoiceNumber: `INV-${t.slug.toUpperCase().slice(0, 6)}-${new Date(at).toISOString().slice(0, 7).replace('-', '')}`,
        amount: plan.priceMonthly, status: failed ? 'FAILED' : 'PAID', method: m % 2 === 0 ? 'CARD' : 'UPI', createdAt: new Date(at).toISOString(),
      });
    }
  });
  return { subscriptions, payments };
}

function seedStore(): PlatformStore {
  const plans = seedPlans();
  const { subscriptions, payments } = seedBilling(plans);
  return {
    plans, subscriptions, payments, auditLog: [],
    settings: {
      platformName: 'MCE — Modular Commerce Engine', supportEmail: 'support@mce.dev', defaultPlanId: 'plan_free',
      maintenanceMode: false, allowNewSignups: true, updatedAt: new Date('2025-01-01T00:00:00Z').toISOString(),
    },
  };
}

function load(): PlatformStore {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as PlatformStore;
      if (parsed.plans?.length) return parsed;
    }
  } catch { /* corrupt or unavailable: reseed */ }
  return seedStore();
}

export const platformRoot = () => (root ??= load());

export function savePlatform() {
  try { localStorage.setItem(KEY, JSON.stringify(platformRoot())); } catch { /* quota/unavailable: still works in memory */ }
}

export function resetPlatform() {
  root = seedStore();
  savePlatform();
}

export const uid = (p: string) => adminUid(p);

/* ------------------------------ request context ------------------------------
   Mirrors admin's ctx(): authenticate -> requireRole(SUPER_ADMIN) -> requirePermission. No tenant scoping
   here BY DESIGN — the platform console is cross-tenant. The real API must still verify the JWT belongs to
   platform staff (a distinct, small set of accounts) on every one of these endpoints. */
export function pctx(perm?: PlatformPermission): { actor: PlatformActor } {
  const user = db.user;
  if (!user) throw new ServiceError('Please sign in to continue', 'UNAUTHORIZED');
  if (!isSuperAdmin(user.role)) throw new ServiceError('This console is for platform staff only.', 'FORBIDDEN');
  if (perm && shouldFail()) throw new ServiceError('Simulated service failure (?fail is set).');
  if (perm && !canPlatform(user.role, perm)) throw new ServiceError('Your platform role does not allow this action.', 'FORBIDDEN');
  return { actor: { user, permissions: PLATFORM_ROLE_PERMISSIONS[user.role] } };
}

export function recordAudit(actor: PlatformActor, action: string, targetType: AuditLogEntry['targetType'], targetId: string, targetLabel: string, summary: string) {
  const d = platformRoot();
  d.auditLog.unshift({ id: uid('aud'), at: new Date().toISOString(), actorName: actor.user.name, actorEmail: actor.user.email, action, targetType, targetId, targetLabel, summary });
  if (d.auditLog.length > 500) d.auditLog.length = 500;
  savePlatform();
}

export const planFeatures = (planId: string) => planOf(planId).features;
export function planOf(planId: string): Plan {
  const p = platformRoot().plans.find((x) => x.id === planId);
  if (!p) throw new ServiceError('Plan not found', 'NOT_FOUND');
  return p;
}

/**
 * THE canonical recompute step: Plan Feature + Tenant Override = Effective Feature. Called after every
 * mutation that can change a tenant's effective flags (plan change, override change, plan feature edit).
 * Writes into the SAME `TenantData.features` cache every other read site already trusts — no second copy.
 */
export function syncTenantFeatures(tenantId: string) {
  const t = dataFor(tenantId);
  const plan = platformRoot().plans.find((p) => p.id === t.planId) ?? platformRoot().plans[0];
  t.features = computeEffectiveFeatures(plan.features, t.featureOverrides);
  saveAdmin();
}

export function syncAllTenantsOnPlan(planId: string) {
  Object.keys(adminRoot()).forEach((tid) => { if (dataFor(tid).planId === planId) syncTenantFeatures(tid); });
}

export function paginate<T>(items: T[], page = 1, pageSize = 10) {
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const p = Math.min(Math.max(1, page), totalPages);
  return { items: items.slice((p - 1) * pageSize, p * pageSize), total, page: p, pageSize, totalPages };
}

export const matches = (q: string | undefined, ...fields: (string | undefined)[]) => {
  const t = q?.trim().toLowerCase();
  return !t || fields.some((f) => f?.toLowerCase().includes(t));
};

/** Shared list-row shape for a tenant, used by both the Tenants list and dashboard "recent tenants". */
export function tenantRow(t: TenantData): TenantListRow {
  const plan = planOf(t.planId);
  const r = buildReport(t, '30d');
  return {
    tenantId: t.tenantId, name: t.name, slug: t.slug, status: t.status, planId: t.planId, planName: plan.name,
    ownerName: t.ownerName, ownerEmail: t.ownerEmail, createdAt: t.createdAt, customers: t.customers.length,
    orders30d: r.kpis.orders.value, revenue30d: r.kpis.revenue.value, overrideCount: Object.keys(t.featureOverrides).length,
  };
}

export type { FeatureKey };
